import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// Pre-construction master sub list — the Grass Creek prototype's "Master sub
// list" page. The server hands over the whole list as data; this draws it by
// division (each sub once per division, a label per subdivision, or one blue
// label when they do the whole division — "09" — which counts as every
// subdivision in it except any left out: "-09 91 00"; clicking that label
// opens a quick editor for the division right there), and does
// search, the area and subdivision filters, expand/collapse and the open row
// (edit in place, your own rating, notes, files) right here, so they're
// instant. Every change goes to the server and answers with just that sub;
// ratings show before the answer (and roll back if it fails). A switch next
// to search shows the list By division (the default) or as All subs: each
// sub once, A to Z, with everything they do on its row (remembered in this
// browser).
// Pop-ups: Add a sub / Edit sub, Remove, Import your sub list, Delete entire
// list (the ⋮ menu) and History (every change; deleted subs come back).
// Files and spreadsheets go straight to S3: presign → PUT → register.

const PICK_LIMIT = 24
const RISK_RANK = { low: 1, medium: 2, high: 3 }
const RISK_CLS = { high: "b-red", medium: "b-amber", low: "b-green" }
const NONE = "__none"
// The row key of a sub in the All subs view (one row per sub, no division).
const ALL = "__all"
const VIEW_KEY = "conslog.precon.subs.view"

export default class extends Controller {
  static targets = ["stats", "search", "areas", "cf", "cfOn", "list", "data", "i18n",
    "addModal", "addDialog", "addTitle", "addForm", "addIns", "addInsDate", "addCodesLabel", "addCodeSearch", "addCodes", "addRate", "addRemove", "addSave",
    "rmModal", "rmDialog", "rmTitle", "rmText", "rmJobs", "rmGo",
    "impModal", "impDrop", "impResult", "impLists", "impListsBtn", "impGo",
    "moreWrap", "moreMenu", "raModal", "raText", "raGo", "histModal", "histTabs", "histBody", "histFoot", "tagPop", "view", "divTools"]

  static values = { createUrl: String, subUrl: String, presignUrl: String, importPreviewUrl: String, importUrl: String, importListsUrl: String,
    historyUrl: String, removeAllUrl: String, restoreAllUrl: String, restoreUrl: String }

  connect() {
    const data = JSON.parse(this.dataTarget.textContent || "{}")
    this.L = JSON.parse(this.i18nTarget.textContent || "{}")
    this.icons = this.L.icons || {}
    this.subs = data.subs || []
    this.meName = data.me || ""
    this.divNames = data.divisions || {}
    this.csi = data.csi || []
    this.csiName = {}
    this.csiCommon = {}
    this.csi.forEach(([code, name, common]) => { this.csiName[code] = name; if (common) this.csiCommon[code] = true })
    // How many subdivisions each division holds (a whole division counts them all).
    this.divSize = {}
    this.csi.forEach(([code]) => { const dv = code.slice(0, 2); this.divSize[dv] = (this.divSize[dv] || 0) + 1 })

    this.q = ""; this.area = "all"; this.cf = new Set(); this.closed = new Set()
    this.openKey = null; this.draft = null; this.pick = false; this.pickQ = ""; this.noteDraft = ""
    this.cfOpen = false; this.cfQ = ""
    this.view = "division"
    try { if (window.localStorage.getItem(VIEW_KEY) === "all") this.view = "all" } catch (e) { /* not remembered */ }
    this.uploads = {}; this.seq = 0
    this.add = null; this.rmId = null; this.imp = null; this.hist = null; this.tp = null

    this._click = this._onClick.bind(this)
    this._input = this._onInput.bind(this)
    this._change = this._onChange.bind(this)
    this._keydown = this._onKeydown.bind(this)
    this._drag = this._onDrag.bind(this)
    ;[this.listTarget, this.areasTarget, this.cfTarget, this.cfOnTarget, this.addDialogTarget, this.tagPopTarget].forEach(el => {
      el.addEventListener("click", this._click)
      el.addEventListener("input", this._input)
      el.addEventListener("change", this._change)
      el.addEventListener("keydown", this._keydown)
    })
    ;["dragenter", "dragover", "dragleave", "drop"].forEach(t => this.listTarget.addEventListener(t, this._drag))
    this._escape = (e) => { if (e.key === "Escape") this._closeTop() }
    document.addEventListener("keydown", this._escape)
    // A file dropped next to a drop zone mustn't open in the browser (and leave the page).
    this._docDrop = (e) => { if (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes("Files")) e.preventDefault() }
    document.addEventListener("dragover", this._docDrop)
    document.addEventListener("drop", this._docDrop)

    this.render()

    if (data.imported) this._toast(this.t("imp_lists_done", { directory: data.imported[0], sub_bids: data.imported[1] }))
    if (data.open) this._deepOpen(Number(data.open))
    if (data.addCode) this.openAdd(null, String(data.addCode))
    if (data.edit) this.openEdit(Number(data.edit))
  }

  disconnect() {
    this._closeMore()
    document.removeEventListener("keydown", this._escape)
    document.removeEventListener("dragover", this._docDrop)
    document.removeEventListener("drop", this._docDrop)
  }

  // ── Words ─────────────────────────────────────────────────────────────
  t(key, vars = {}) {
    let v = key.split(".").reduce((o, k) => (o == null ? o : o[k]), this.L)
    if (v && typeof v === "object") v = (vars.count === 1 && v.one != null) ? v.one : (v.other != null ? v.other : v.one)
    if (v == null) return key
    return String(v).replace(/%\{(\w+)\}/g, (_, k) => (vars[k] != null ? vars[k] : ""))
  }
  te(key, vars) { return this._e(this.t(key, vars)) }
  _e(s) { return String(s == null ? "" : s).replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])) }

  // ── Lookups ───────────────────────────────────────────────────────────
  sub(id) { return this.subs.find(s => s.id === Number(id)) }
  // A label is a subdivision ("09 29 00"), a whole division ("09"), or a
  // subdivision left out of that whole division ("-09 91 00").
  isDiv(c) { return String(c).length === 2 }
  isOut(c) { return String(c)[0] === "-" }
  codeName(c) { return this.isDiv(c) ? (this.divNames[c] || "") : (this.csiName[c] || "") }
  codeLabel(c) {
    if (this.isDiv(c)) return this.t("div_tag", { code: c, name: this.divNames[c] || "" })
    if (this.isOut(c)) return this.t("out_tag", { label: this.codeLabel(c.slice(1)) })
    const n = this.codeName(c); return c + (n ? " " + n : "")
  }
  shortName(c) {
    if (this.isDiv(c)) return this.t("div_tag_short", { name: this.divNames[c] || c })
    return String(this.codeName(c) || this.t("cf_btn")).split(/,\s*| & | and /)[0]
  }
  // Do these labels cover the cost code: the subdivision itself, or its
  // whole division unless it was left out?
  covers(codes, c) { return codes.includes(c) || (codes.includes(c.slice(0, 2)) && !codes.includes("-" + c)) }
  // How many subdivisions the labels add up to (a whole division counts every one in it, less the ones left out).
  coverCount(codes) { return codes.reduce((n, c) => n + (this.isDiv(c) ? (this.divSize[c] || 1) : this.isOut(c) ? -1 : 1), 0) }
  // The subdivisions a sub left out of a whole division, without the "-".
  outsOf(codes, dv) { return codes.filter(k => this.isOut(k) && k.slice(1, 3) === dv).map(k => k.slice(1)) }
  divLabel(dv) { return dv === NONE ? this.t("no_codes_div") : `${dv} ${this.divNames[dv] || this.t("division", { code: dv })}` }
  stars(n) { return "★★★★★".slice(0, n) + "☆☆☆☆☆".slice(0, 5 - n) }
  riskLabel(r) { return r ? this.t(`risk_${r}`) : this.t("no_risk") }
  history(s) { return s.bids > 0 ? this.t("history", { bids: s.bids, wins: s.wins }) : this.t("new_to_you") }
  segOf(s) { return s.ins }

  insInfo(s) {
    if (s.ins === "expiring") return [s.insDate ? this.t("ins_expires_on", { date: s.insDate }) : this.t("ins_expiring"), "b-amber"]
    if (s.ins === "expired") return [s.insDate ? this.t("ins_expired_on", { date: s.insDate }) : this.t("ins_expired"), "b-red"]
    if (s.ins === "missing") return [this.t("ins_missing"), "b-red"]
    return [this.t("ins_ok"), "b-green"]
  }

  // Everyone's ratings → average stars, worst risk, a line per person.
  rateOf(s) {
    const people = (s.rates || []).filter(r => r.stars)
    if (!people.length) return { has: false, n: 0 }
    const avg = people.reduce((a, r) => a + r.stars, 0) / people.length
    const worst = people.reduce((a, r) => ((RISK_RANK[r.risk] || 0) > (RISK_RANK[a] || 0) ? r.risk : a), "")
    return {
      has: true, n: people.length, starsText: this.stars(Math.round(avg)), riskText: this.riskLabel(worst), riskCls: RISK_CLS[worst] || "b-gray",
      tips: people.map(r => ({ who: r.who, line: this.stars(r.stars) + (r.risk ? " · " + this.riskLabel(r.risk) : ""), note: r.note || "", at: r.at ? this.t("rated_on", { date: r.at }) : "" }))
    }
  }
  mine(s) { return (s.rates || []).find(r => r.me) || { stars: 0, risk: "", note: "" } }

  // byCode / allCodes / byDivCodes: the subdivisions subs are listed under;
  // wholeDiv: the subs who do each whole division; divs: every division with
  // anyone in it; nFor(code): how many subs do a subdivision, counting the
  // ones who do its whole division.
  _index() {
    const byCode = {}
    const wholeDiv = {}
    this.subs.forEach(s => s.codes.forEach(c => {
      if (this.isOut(c)) return
      const into = this.isDiv(c) ? wholeDiv : byCode
      ;(into[c] = into[c] || []).push(s)
    }))
    const allCodes = Object.keys(byCode).sort()
    const byDivCodes = {}
    allCodes.forEach(c => { const dv = c.slice(0, 2); (byDivCodes[dv] = byDivCodes[dv] || []).push(c) })
    const divs = [...new Set([...Object.keys(byDivCodes), ...Object.keys(wholeDiv)])].sort()
    const nFor = c => (byCode[c] || []).length + (wholeDiv[c.slice(0, 2)] || []).filter(s => !s.codes.includes("-" + c)).length
    return { byCode, allCodes, byDivCodes, wholeDiv, divs, nFor }
  }

  _filters() {
    const q = this.q.trim().toLowerCase()
    const qs = q.replace(/[\s.-]/g, "")
    const qNum = !!qs && /^\d+$/.test(qs)
    const cfOn = [...this.cf].sort()
    const main = this._mainAreas()
    return {
      q, cfOn, filtering: !!q || this.area !== "all" || cfOn.length > 0,
      areaOk: s => this.area === "all" || (this.area === NONE ? !main.includes(s.area) : s.area === this.area),
      // A sub's whole division matches its number (also inside a longer one: "0929"), its name, and any
      // subdivision's name in it — but not a subdivision they left out.
      codeHit: (c, s) => !!q && (this.isDiv(c)
        ? this._divHit(s, c, q, qs, qNum)
        : (qNum ? c.replace(/\s/g, "").startsWith(qs) : this.codeName(c).toLowerCase().includes(q))),
      textHit: s => `${s.name} ${s.contact} ${s.area}`.toLowerCase().includes(q)
    }
  }

  _divHit(s, c, q, qs, qNum) {
    const outs = s ? this.outsOf(s.codes, c) : []
    if (qNum) return c.startsWith(qs) || (qs.startsWith(c) && !outs.some(k => k.replace(/\s/g, "").startsWith(qs)))
    if (this.codeName(c).toLowerCase().includes(q)) return true
    return this.csi.some(([k, n]) => k.slice(0, 2) === c && !outs.includes(k) && String(n).toLowerCase().includes(q))
  }

  // The four areas most subs work out of; everything else is "Other".
  _mainAreas() {
    const count = {}
    this.subs.forEach(s => { if (s.area) count[s.area] = (count[s.area] || 0) + 1 })
    return Object.keys(count).sort((a, b) => count[b] - count[a] || a.localeCompare(b)).slice(0, 4)
  }

  // ── Drawing ───────────────────────────────────────────────────────────
  render() {
    this._renderView()
    this._renderStats()
    this._renderAreas()
    this._renderCf()
    this._renderCfOn()
    this._renderList()
  }

  // By division / All subs: the switch, and Expand / Collapse all (division view only).
  _renderView() {
    const all = this.view === "all"
    this.viewTarget.querySelectorAll("[data-v]").forEach(b => { const on = b.dataset.v === this.view; b.classList.toggle("on", on); b.setAttribute("aria-pressed", on) })
    this.divToolsTargets.forEach(el => { el.style.display = all ? "none" : "" })
  }

  setView(e) {
    const v = e.currentTarget.dataset.v === "all" ? "all" : "division"
    if (v === this.view) return
    if (this.tp) this.closeTagPop()
    if (this.openKey) this.closeRow()
    this.view = v
    try { window.localStorage.setItem(VIEW_KEY, v) } catch (err) { /* not remembered */ }
    track("Precon Sub List View Switched", { view: v, subs: this.subs.length })
    this._renderView()
    this._renderList()
  }

  // A sub's labels for its All subs row: whole divisions and subdivisions, by division.
  _tagsOf(s) { return this._sortTags(s.codes.filter(c => !this.isOut(c))) }
  // …and the ones from its other divisions, for its row under division dv.
  _othersOf(s, dv) { return dv === NONE || dv === ALL ? [] : this._tagsOf(s).filter(c => c.slice(0, 2) !== dv) }

  _renderStats() {
    const ix = this._index()
    const stat = (l, n, s) => `<div class="stat"><div class="statl">${this._e(l)}</div><div class="statn">${this._e(n)}</div><div class="stats-s">${this._e(s)}</div></div>`
    const divs = ix.divs.length
    this.statsTarget.innerHTML =
      stat(this.t("stat_subs"), this.subs.length, this.t("stat_subs_sub", { count: divs })) +
      stat(this.t("stat_codes"), ix.allCodes.length, this.t("stat_codes_sub")) +
      stat(this.t("stat_thin"), ix.allCodes.filter(c => ix.nFor(c) < 2).length, this.t("stat_thin_sub")) +
      stat(this.t("stat_ins"), this.subs.filter(s => s.ins !== "ok").length, this.t("stat_ins_sub"))
  }

  _renderAreas() {
    const main = this._mainAreas()
    const list = ["all", ...main]
    if (this.subs.some(s => !main.includes(s.area))) list.push(NONE)
    if (!list.includes(this.area)) this.area = "all"
    this.areasTarget.innerHTML = list.map(a => `<button class="fchip ${this.area === a ? "on" : ""}" type="button" data-act="area" data-v="${this._e(a)}">${this._e(a === "all" ? this.t("all_areas") : a === NONE ? this.t("other_area") : a)}</button>`).join("")
  }

  _cfLabel() {
    const on = [...this.cf].sort()
    if (!on.length) return this.t("cf_btn")
    if (on.length > 1) return this.t("cf_n", { count: on.length })
    return this.isDiv(on[0]) ? this.divLabel(on[0]) : `${on[0]} ${this.shortName(on[0])}`
  }

  // The filter: a subdivision shows the subs who do it (theirs, or their whole
  // division's unless they left it out); a division shows anyone who does any
  // of it.
  _cfMatch(s, c) {
    return this.isDiv(c) ? s.codes.some(k => !this.isOut(k) && k.slice(0, 2) === c) : this.covers(s.codes, c)
  }

  _renderCf() {
    const on = this.cf.size > 0
    let html = `<button class="fchip mlfbtn ${on ? "on" : ""}" type="button" data-act="cf-toggle" aria-haspopup="true" aria-expanded="${this.cfOpen}"><span data-part="cflabel">${this._e(this._cfLabel())}</span>${this.icons.down}</button>`
    if (this.cfOpen) {
      html += `<div class="menuback" data-act="cf-close"></div>
        <div class="colmenu mlfmenu" role="dialog" aria-label="${this.te("cf_aria")}">
          <div class="mlfmh"><span>${this.te("cf_head")}</span><button class="linkbtn" type="button" data-act="cf-clear" ${on ? "" : "disabled"}>${this.te("clear")}</button></div>
          <div class="mlfms"><span class="search wide">${this.icons.search}<input class="inp" type="text" value="${this._e(this.cfQ)}" data-input="cfq" placeholder="${this.te("cf_search")}" aria-label="${this.te("cf_search_aria")}"></span></div>
          <div class="mlfml" data-part="cfrows">${this._cfRows()}</div>
          <div class="mlfmf"><span class="muted" data-part="cffoot">${this._e(on ? this.t("cf_foot_n", { count: this.cf.size }) : this.t("cf_foot_none"))}</span><button class="btn btn-primary btn-sm" type="button" data-act="cf-close">${this.te("done")}</button></div>
        </div>`
    }
    this.cfTarget.innerHTML = html
  }

  _cfRows() {
    const ix = this._index()
    const q = this.cfQ.trim().toLowerCase()
    const qs = q.replace(/[\s.-]/g, "")
    const qNum = !!qs && /^\d+$/.test(qs)
    const match = c => !q || (qNum ? c.replace(/\s/g, "").startsWith(qs) : `${this.codeName(c)} ${this.divNames[c.slice(0, 2)] || ""}`.toLowerCase().includes(q))
    let html = ""
    ix.divs.forEach(dv => {
      const cs = (ix.byDivCodes[dv] || []).filter(match)
      const divHit = !q || (qNum ? dv.startsWith(qs.slice(0, 2)) || qs.startsWith(dv) : this.divLabel(dv).toLowerCase().includes(q))
      if (!cs.length && !divHit) return
      // The division itself is a choice too: anyone who does any of it.
      const label = this.divLabel(dv)
      html += `<label class="colopt mlfdv mlfdvo"><input type="checkbox" ${this.cf.has(dv) ? "checked" : ""} data-cf="${dv}" aria-label="${this._e(label)}"><span class="grow">${this._e(label)}</span><span class="muted">${this.subs.filter(s => this._cfMatch(s, dv)).length}</span></label>`
      cs.forEach(c => {
        const label = this.codeLabel(c)
        html += `<label class="colopt"><input type="checkbox" ${this.cf.has(c) ? "checked" : ""} data-cf="${c}" aria-label="${this._e(label)}"><span class="grow">${this._e(label)}</span><span class="muted">${ix.nFor(c)}</span></label>`
      })
    })
    return html || `<div class="mlfnone muted">${this.te("cf_none")}</div>`
  }

  _renderCfOn() {
    const on = [...this.cf].sort()
    if (!on.length) { this.cfOnTarget.innerHTML = ""; return }
    this.cfOnTarget.innerHTML = `<div class="mlfon"><span class="muted">${this.te(on.length === 1 ? "cf_on_one" : "cf_on_many")}</span>${on.map(c => {
      const label = this.isDiv(c) ? this.divLabel(c) : this.codeLabel(c)
      return `<span class="fchipn"><span class="trunc">${this._e(label)}</span><button class="fx" type="button" data-act="cf-remove" data-code="${c}" aria-label="${this.te("cf_stop", { label })}">${this.icons.x}</button></span>`
    }).join("")}<button class="linkbtn" type="button" data-act="cf-clear">${this.te("clear")}</button></div>`
  }

  _renderList() {
    if (this.view === "all") return this._renderAll()
    const ix = this._index()
    const f = this._filters()
    const out = []
    const block = (dv, codes, subs, inDv) => {
      const shown = subs.filter(s => {
        if (!f.areaOk(s)) return false
        const mine = inDv(s)
        if (f.cfOn.length && !f.cfOn.some(c => c.slice(0, 2) === dv && this._cfMatch(s, c))) return false
        return !f.q || f.textHit(s) || mine.some(c => f.codeHit(c, s))
      })
      if (f.filtering && !shown.length) return
      const closed = this.closed.has(dv) && !f.filtering
      out.push(this._divRow(dv, codes, subs, shown, closed, ix, f))
      if (closed) return
      shown.forEach(s => {
        const open = this.openKey === `${dv}:${s.id}`
        out.push(this._subRow(s, dv, inDv(s), f, open, this._othersOf(s, dv)))
        if (open) out.push(this._panel(s, dv))
      })
    }
    ix.divs.forEach(dv => {
      const codes = ix.byDivCodes[dv] || []
      // Subs who do the whole division first, then the rest by name.
      const all = this.subs.filter(s => s.codes.some(c => c.slice(0, 2) === dv))
      const subs = all.filter(s => s.codes.includes(dv)).concat(all.filter(s => !s.codes.includes(dv)))
      block(dv, codes, subs, s => s.codes.filter(c => c.slice(0, 2) === dv).sort())
    })
    const none = this.subs.filter(s => !s.codes.length)
    if (none.length && !f.cfOn.length) block(NONE, [], none, () => [])
    if (!out.length) out.push(`<div class="pad"><div class="empty">${this.te(this.subs.length ? "no_match" : "empty")}</div></div>`)
    this.listTarget.innerHTML = out.join("")
    if (this.pick) this._renderPick()
  }

  // All subs: each sub once, A to Z, with everything they do on its row.
  _renderAll() {
    const f = this._filters()
    const out = []
    this.subs.forEach(s => {
      if (!f.areaOk(s)) return
      if (f.cfOn.length && !f.cfOn.some(c => this._cfMatch(s, c))) return
      const tags = this._tagsOf(s)
      if (f.q && !f.textHit(s) && !tags.some(c => f.codeHit(c, s))) return
      const open = this.openKey === `${ALL}:${s.id}`
      out.push(this._subRow(s, ALL, tags, f, open))
      if (open) out.push(this._panel(s, ALL))
    })
    if (out.length) out.push(`<div class="mlallfoot muted">${this.te("n_subs", { count: out.filter(h => h.includes("mlx")).length })}</div>`)
    else out.push(`<div class="pad"><div class="empty">${this.te(this.subs.length ? "no_match" : "empty")}</div></div>`)
    this.listTarget.innerHTML = out.join("")
    if (this.pick) this._renderPick()
  }

  _divRow(dv, codes, subs, shown, closed, ix, f) {
    const label = this.divLabel(dv)
    let meta = this.t("n_subs", { count: subs.length })
    if (dv === NONE) meta += " · " + this.t("no_codes_meta")
    else if (codes.length) meta += " · " + this.t("n_codes", { count: codes.length })
    const whole = dv === NONE ? 0 : (ix.wholeDiv[dv] || []).length
    const wholeHtml = whole ? `<span class="mldtn">${this.te("n_whole", { count: whole })}</span>` : ""
    if (f.filtering) meta += " · " + this.t("showing", { count: shown.length })
    let act = ""
    if (dv !== NONE) {
      const thin = codes.filter(c => ix.nFor(c) < 2)
      if (thin.length) {
        const text = thin.length === 1 ? this.t("thin_one", { name: this.shortName(thin[0]) }) : this.t("thin_many", { count: thin.length })
        act += `<span class="badge b-amber" title="${this.te("thin_tip", { codes: thin.map(c => this.codeLabel(c)).join(", ") })}">${this._e(text)}</span>`
      }
      act += `<button class="linkbtn" type="button" data-act="add-in" data-dv="${dv}" aria-label="${this.te("add_here_aria", { division: label })}">${this.icons.plus}${this.te("add_here")}</button>`
    }
    return `<div class="mldivrow"><button class="mldiv" type="button" data-act="div-toggle" data-dv="${dv}" aria-label="${this.te(closed ? "open_div" : "close_div", { division: label })}"><span class="chev ${closed ? "" : "open"}">${this.icons.chev}</span><b>${this._e(label)}</b><span class="muted">${this._e(meta)}</span>${wholeHtml}</button><span class="mldivact">${act}</span></div>`
  }

  // codes: the labels of the division the row sits under (all of them in All
  // subs); others: the sub's labels from its other divisions, shown after
  // them in a lighter look so a row always shows everything the sub does.
  _subRow(s, dv, codes, f, open, others = []) {
    const [insLabel, insCls] = this.insInfo(s)
    const rt = this.rateOf(s)
    const notes = s.notes || []
    const files = s.files || []
    const name = this._e(s.name)
    let badges = ""
    if (s.manualNew) {
      const tip = (s.addedBy ? this.t("manual_tip_by", { who: s.addedBy, date: s.addedAt }) : this.t("manual_tip_on", { date: s.addedAt })) + " " + this.t("manual_tip_end")
      badges += `<span class="badge b-brand" title="${this._e(tip)}">${this.te("manual_new")}</span>`
    }
    const tag = (c, oth) => {
      // A subdivision opens its division's editor too, to add more in that division.
      if (!this.isDiv(c)) return `<button class="mltag ${oth ? "oth" : ""} ${f.cfOn.includes(c) || f.cfOn.includes(c.slice(0, 2)) || f.codeHit(c) ? "hit" : ""}" type="button" data-act="tag-edit" data-dv="${c.slice(0, 2)}" data-id="${s.id}" title="${this._e(this.codeLabel(c))}" aria-label="${this.te("tp_aria", { division: this.divLabel(c.slice(0, 2)), name: s.name })}"><span class="mltc">${c}</span><span class="trunc">${this._e(this.shortName(c))}</span></button>`
      const hit = f.cfOn.includes(c) || f.cfOn.some(k => k.slice(0, 2) === c && this.covers(s.codes, k)) || f.codeHit(c, s)
      // "All of Finishes" (solid); with some taken out, "Finishes 24 of 33" (dashed).
      // A click opens the division's quick editor, not the row.
      const outs = this.outsOf(s.codes, c)
      const total = this.divSize[c] || 0
      const n = total - outs.length
      let text = `<span class="trunc">${this._e(this.shortName(c))}</span>`
      let tip = this.t("div_tag_tip", { division: this.divLabel(c) })
      if (outs.length) {
        text = `<span class="trunc">${this._e(this.divNames[c] || c)}</span><span class="mldtc">${this.te("div_count", { n, total })}</span>`
        tip = this.t("div_part_tip", { n, total, division: this.divLabel(c) }) + ". " + this.t("tip_out", { list: outs.map(k => this.codeLabel(k)).join(", ") })
      }
      return `<button class="mltag mldt ${outs.length ? "part" : ""} ${oth ? "oth" : ""} ${hit ? "hit" : ""}" type="button" data-act="tag-edit" data-dv="${c}" data-id="${s.id}" title="${this._e(tip)}" aria-label="${this.te("tp_aria", { division: this.divLabel(c), name: s.name })}">${this.icons.stack || ""}<span class="mltc">${c}</span>${text}</button>`
    }
    const tags = codes.map(c => tag(c, false)).join("") + others.map(c => tag(c, true)).join("")
    let rating
    if (rt.has) {
      const tips = rt.tips.map(x => `<span class="rtip-r"><span class="rtip-w"><b>${this._e(x.who)}</b> · ${this._e(x.line)}</span>${x.note ? `<span class="rtip-n">“${this._e(x.note)}”</span>` : ""}<span class="rtip-m">${this._e(x.at)}</span></span>`).join("")
      rating = `<span class="rtw" tabindex="0"><span class="rstars">${rt.starsText}</span><span class="badge ${rt.riskCls}">${this._e(rt.riskText)}</span><span class="rtip" role="tooltip"><span class="rtip-h">${this.te("tip_ratings")}</span>${tips}</span></span>`
    } else {
      rating = `<button class="linkbtn mute" type="button" data-act="row-open" data-dv="${dv}" data-id="${s.id}">${this.te("rate")}</button>`
    }
    let notesCell
    if (notes.length) {
      const last = notes[notes.length - 1]
      const tips = notes.slice().reverse().map(n => `<span class="rtip-r"><span class="rtip-w"><b>${this._e(n.by)}</b> · ${this._e(n.at)}</span><span class="rtip-n">${this._e(n.text)}</span></span>`).join("")
      notesCell = `<span class="rtw notew" tabindex="0"><span class="trunc">${this._e(last.text)}</span>${notes.length > 1 ? `<span class="badge b-gray">+${notes.length - 1}</span>` : ""}<span class="rtip" role="tooltip"><span class="rtip-h">${this.te("tip_notes")}</span>${tips}</span></span>`
    } else {
      notesCell = `<button class="linkbtn mute" type="button" data-act="row-open" data-dv="${dv}" data-id="${s.id}">${this.te("add_note_row")}</button>`
    }
    if (files.length) {
      const aria = this.te("n_files_on", { count: files.length, name: s.name })
      notesCell += `<span class="mlclip" title="${aria}" aria-label="${aria}">${this.icons.clip}${files.length}</span>`
    }
    const tagsHtml = tags || (dv === ALL ? `<span class="muted">${this.te("no_codes_div")}</span>` : "")
    return `<div class="t6 mlg sub mlx ${open ? "open" : ""}" data-act="row-toggle" data-dv="${dv}" data-id="${s.id}">
      <span class="lnm"><button class="chev ${open ? "open" : ""}" type="button" data-act="row-toggle" data-dv="${dv}" data-id="${s.id}" aria-expanded="${open}" aria-label="${this.te(open ? "close_row" : "open_row", { name: s.name })}">${this.icons.chev}</button><span class="strong trunc" title="${name}">${name}</span>${badges}</span>
      <span class="mltags">${tagsHtml}</span>
      <span class="mlct"><span class="trunc">${this._e(s.contact)}</span>${s.email ? "" : `<span class="badge b-amber">${this.te("needs_email")}</span>`}</span>
      <span class="trunc">${this._e(s.area || "—")}</span>
      <span class="muted trunc">${this._e(this.history(s))}</span>
      <span><span class="badge ${insCls}">${this._e(insLabel)}</span></span>
      <span>${rating}</span>
      <span class="mlnotes">${notesCell}</span>
    </div>`
  }

  // Redraw one sub's rows (every division it shows in) without touching the rest.
  _refreshRows(id) {
    const s = this.sub(id)
    if (!s) return
    const f = this._filters()
    this.listTarget.querySelectorAll(`.mlx[data-id="${s.id}"]`).forEach(row => {
      const dv = row.dataset.dv
      const codes = dv === NONE ? [] : dv === ALL ? this._tagsOf(s) : s.codes.filter(c => c.slice(0, 2) === dv).sort()
      row.outerHTML = this._subRow(s, dv, codes, f, this.openKey === `${dv}:${s.id}`, this._othersOf(s, dv))
    })
  }

  // ── The open row ──────────────────────────────────────────────────────
  _panel(s, dv) {
    const ml = this.te.bind(this)
    const d = this.draft
    const input = (key, label, ph) => `<label class="fl">${ml(label)}<input class="inp" type="text" value="${this._e(d[key])}" data-draft="${key}" placeholder="${ml(ph)}"></label>`
    return `<div class="mlpanel" role="region" aria-label="${ml("details_aria", { name: s.name })}" data-panel="${dv}:${s.id}">
      <div class="mlpcol"><div class="mlpsec">
        <div class="mlph"><b>${ml("company_info")}</b></div>
        <div class="two">${input("name", "f_company", "ph_company")}${input("contact", "f_contact", "ph_contact")}</div>
        <div class="two">${input("email", "f_email", "ph_email")}${input("phone", "f_phone", "ph_phone")}</div>
        <div class="two">${input("area", "f_area", "ph_area")}<div class="fl">${ml("f_ins")}<div class="seg" data-part="ins">${this._segHtml(d.ins, "draft-ins")}</div><div data-part="insdate">${this._insDateHtml(d.ins, d.insDate, 'data-draft="insDate"')}</div></div></div>
        <div class="fl" data-part="codes">${this._panelCodes(dv)}</div>
        <div class="mlpfoot" data-part="foot">${this._panelFoot(s)}</div>
      </div></div>
      <div class="mlpcol">
        <div class="mlpsec" data-part="ratings">${this._panelRatings(s)}</div>
        <div class="mlpsec" data-part="notes">${this._panelNotes(s)}</div>
        <div class="mlpsec" data-part="files">${this._panelFiles(s)}</div>
      </div>
    </div>`
  }

  // "Expires on" under the Insurance switch, while it says Expiring (or the sub's date has passed).
  _insDateHtml(ins, iso, attr) {
    if (ins !== "expiring" && ins !== "expired") return ""
    return `<label class="fl" style="margin-top: 6px;">${this.te("f_ins_expires")}<span class="dp">${this.icons.calendar}<input class="inp dpi" type="text" value="${this._e(iso || "")}" ${attr} data-controller="datepicker" placeholder="${this.te("ph_date")}" aria-label="${this.te("f_ins_expires")}"></span></label>`
  }

  _segHtml(value, act) {
    return [["ok", "ins_ok"], ["expiring", "ins_expiring"], ["missing", "ins_missing"]]
      .map(([v, k]) => `<button class="segbtn ${value === v ? "on" : ""}" type="button" data-act="${act}" data-v="${v}">${this.te(k)}</button>`).join("")
  }

  _panelCodes(dv) {
    const d = this.draft
    const picked = this._sortTags([...d.codes])
    let html = `<span>${this.te("codes_label")}</span><span class="hint">${this.te("codes_hint")}</span><div class="fchips">`
    // A whole division reads like its label on the list ("09 Finishes 24 of 33")
    // and opens the same editor; what it leaves out shows there, not as chips here.
    html += picked.filter(c => !this.isOut(c)).map(c => {
      const l = this.codeLabel(c)
      const x = `<button class="fx" type="button" data-act="draft-code" data-code="${c}" aria-label="${this.te("take_off", { label: l })}">${this.icons.x}</button>`
      if (!this.isDiv(c)) return `<span class="fchipn mlcc mlsc" role="button" tabindex="0" data-act="tp-draft" data-dv="${c.slice(0, 2)}" title="${this.te("tp_aria", { division: this.divLabel(c.slice(0, 2)), name: this.draft.name })}"><span class="trunc">${this._e(l)}</span>${x}</span>`
      const outs = this.outsOf(picked, c)
      const total = this.divSize[c] || 0
      const text = outs.length ? `<span class="trunc">${this._e(this.codeName(c))}</span><span class="mldtc">${this.te("div_count", { n: total - outs.length, total })}</span>` : `<span class="trunc">${this._e(l)}</span>`
      return `<span class="fchipn mlcc mldc ${outs.length ? "part" : ""}" role="button" tabindex="0" data-act="tp-draft" data-dv="${c}" title="${this.te("tp_aria", { division: this.divLabel(c), name: this.draft.name })}">${this.icons.stack || ""}${outs.length ? `<span class="mltc">${c}</span>` : ""}${text}${x}</span>`
    }).join("")
    if (!this.pick) html += `<button class="fchip fadd" type="button" data-act="pick-toggle">${this.icons.plus}${this.te("add_code")}</button>`
    html += "</div>"
    if (!picked.length) html += `<span class="locerr">${this.te("no_codes_err")}</span>`
    if (this.pick) {
      html += `<div class="mlpick"><span class="search wide">${this.icons.search}<input class="inp" type="text" value="${this._e(this.pickQ)}" data-input="pickq" placeholder="${this.te("pick_ph")}" aria-label="${this.te("pick_aria")}"></span>
        <div class="mlpickh"><span class="grow" data-part="pickhead"></span><button class="linkbtn" type="button" data-act="pick-toggle">${this.te("done")}</button></div>
        <div class="cchips" data-part="pickchips"></div></div>`
    }
    return html
  }

  // This division (whole) and its usual subdivisions, or any division or
  // subdivision that matches the search. Each whole division sorts just
  // ahead of its subdivisions ("09" < "09 21 16").
  _pickList(dv, picked, q) {
    const pool = new Set()
    const qs = q.replace(/[\s.-]/g, "")
    const qNum = !!qs && /^\d+$/.test(qs)
    if (!q) {
      if (dv && dv !== NONE && dv !== ALL) {
        pool.add(dv)
        this.csi.forEach(([c, , common]) => { if (common && c.slice(0, 2) === dv) pool.add(c) })
        this._index().allCodes.forEach(c => { if (c.slice(0, 2) === dv) pool.add(c) })
        picked.forEach(c => { const k = c.replace(/^-/, ""); if (k.slice(0, 2) === dv) pool.add(k) })
      } else {
        this._index().allCodes.forEach(c => pool.add(c))
        picked.forEach(c => pool.add(c.replace(/^-/, "")))
        ;[...pool].forEach(c => pool.add(c.slice(0, 2)))
      }
    } else {
      this.csi.forEach(([c]) => pool.add(c))
      this._index().allCodes.forEach(c => pool.add(c))
      Object.keys(this.divNames).forEach(d => pool.add(d))
    }
    const match = c => {
      if (this.isDiv(c)) return qNum ? c.startsWith(qs) || qs.startsWith(c) : this.codeName(c).toLowerCase().includes(q)
      return qNum ? c.replace(/\s/g, "").startsWith(qs) : this.codeName(c).toLowerCase().includes(q)
    }
    return [...pool].filter(c => !q || match(c)).sort()
  }

  // One chip of a picker: a whole division (blue); a subdivision its whole
  // division covers (light blue, with an × — click to leave it out) or one
  // left out (crossed out, with a + — click to put it back); or a subdivision.
  _pickChip(c, on, act) {
    if (this.isDiv(c)) {
      const sel = on.has(c)
      return `<button class="cchip mldvc ${sel ? "on" : ""}" type="button" data-act="${act}" data-code="${c}" aria-pressed="${sel}">${this.icons.stack || ""}${this.te("div_pick", { code: c, name: this.divNames[c] || "" })}</button>`
    }
    if (on.has(c.slice(0, 2))) {
      const out = on.has("-" + c)
      const tip = out ? this.te("out_tip") : this.te("covered_tip", { division: this.divLabel(c.slice(0, 2)) })
      return `<button class="cchip ${out ? "mlout" : "mlcov"}" type="button" data-act="${act}" data-code="${c}" aria-pressed="${!out}" title="${tip}"><span class="mlt">${this._e(this.codeLabel(c))}</span>${out ? this.icons.plus || "" : this.icons.x || ""}</button>`
    }
    const sel = on.has(c)
    return `<button class="cchip ${sel ? "on" : ""}" type="button" data-act="${act}" data-code="${c}" aria-pressed="${sel}">${this._e(this.codeLabel(c))}</button>`
  }

  // ── A whole division's quick editor (click its blue label on a row) ───
  // Floats under the label without opening the row: every subdivision of
  // the division, × to take one out, + to put it back. Each click saves on
  // its own ("Saving… / Saved" in its head). Opened from the open row's chip
  // (fromDraft) each click goes straight into that row's unsaved picks.
  openTagPop(el, id, dv, fromDraft = false) {
    const s = this.sub(id)
    if (!s) return
    const base = fromDraft && this.draft ? [...this.draft.codes] : s.codes.slice()
    const box = this.tagPopTarget.getBoundingClientRect()
    const r = el.getBoundingClientRect()
    const width = Math.min(760, box.width - 16)
    const left = Math.max(8, Math.min(r.left - box.left, box.width - width - 8))
    this.tp = { id: s.id, dv, codes: new Set(base), base, draft: fromDraft, q: "", top: r.bottom - box.top + 8, left, width, arrow: Math.max(12, r.left - box.left - left + 14) }
    this._renderTagPop()
    const i = this.tagPopTarget.querySelector('[data-input="tpq"]')
    if (i) i.focus()
  }

  closeTagPop() {
    const saved = this.tp && this.tp.saved
    this.tp = null
    this.tagPopTarget.innerHTML = ""
    // The list redraws once on close: the sub may have left or joined a division.
    if (saved) this.render()
  }

  // How many of the division's subdivisions the picks cover.
  _tpCount(set, dv) {
    const total = this.divSize[dv] || 0
    if (set.has(dv)) return total - [...set].filter(k => this.isOut(k) && k.slice(1, 3) === dv).length
    return [...set].filter(k => !this.isOut(k) && !this.isDiv(k) && k.slice(0, 2) === dv).length
  }

  // Every subdivision of the division (and any they have that isn't on the CSI list).
  _tpCodes(dv) {
    const list = new Set(this.csi.filter(([c]) => c.slice(0, 2) === dv).map(([c]) => c))
    this.tp.codes.forEach(c => { const k = c.replace(/^-/, ""); if (!this.isDiv(k) && k.slice(0, 2) === dv) list.add(k) })
    return [...list].sort()
  }

  _renderTagPop() {
    const p = this.tp
    if (!p) return this.closeTagPop()
    const div = this.divLabel(p.dv)
    this.tagPopTarget.innerHTML = `<div class="menuback" data-act="tp-close"></div>
      <div class="colmenu mltp" role="dialog" aria-label="${this.te("tp_aria", { division: div, name: this.sub(p.id).name })}" style="top: ${p.top}px; left: ${p.left}px; width: ${p.width}px;">
        <span class="mltparrow" style="left: ${p.arrow}px;"></span>
        <div class="mltph"><div><b>${this._e(div)}</b> <span class="muted" data-part="tphead"></span></div><span class="grow"></span><span class="mltpst" data-part="tpstatus" aria-live="polite"></span><button class="linkbtn mute" type="button" data-act="tp-close" aria-label="${this.te("close")}">${this.icons.x}</button></div>
        <div class="mltps"><span class="search wide">${this.icons.search}<input class="inp" type="text" data-input="tpq" placeholder="${this.te("tp_search", { division: this.divNames[p.dv] || div })}" aria-label="${this.te("tp_search", { division: this.divNames[p.dv] || div })}"></span></div>
        <div class="mltptip" data-part="tptip"></div>
        <div class="cchips mltpchips" data-part="tpchips"></div>
      </div>`
    this._renderTagPopParts()
  }

  _renderTagPopParts() {
    const p = this.tp
    if (!p) return
    const part = name => this.tagPopTarget.querySelector(`[data-part="${name}"]`)
    const q = p.q.trim().toLowerCase()
    const qs = q.replace(/[\s.-]/g, "")
    const qNum = !!qs && /^\d+$/.test(qs)
    const codes = this._tpCodes(p.dv).filter(c => !q || (qNum ? c.replace(/\s/g, "").startsWith(qs) : this.codeName(c).toLowerCase().includes(q)))
    part("tphead").textContent = "· " + this.t("tp_head", { name: this.sub(p.id).name, n: this._tpCount(p.codes, p.dv), total: this.divSize[p.dv] || 0 })
    part("tptip").textContent = this.t(p.codes.has(p.dv) ? "tp_tip_div" : "tp_tip_one")
    part("tpchips").innerHTML = [p.dv, ...codes].map(c => this._pickChip(c, p.codes, "tp-code")).join("")
  }

  _tpStatus(key) {
    const el = this.tagPopTarget.querySelector('[data-part="tpstatus"]')
    if (el) el.textContent = key ? this.t(key) : ""
  }

  // One click in the editor: into the open row's picks (fromDraft), or saved
  // right away. A sub always keeps at least one label.
  _tpToggle(code) {
    const p = this.tp
    const next = new Set(p.codes)
    this._toggleCode(next, code)
    if (![...next].some(c => !this.isOut(c))) return this._toast(this.t("t_give_code"), "error")
    p.codes = next
    this._renderTagPopParts()
    if (!p.draft) return this._tpSave()
    if (this.draft && this.draft.id === p.id) {
      this.draft.codes = new Set(next)
      this._panelPart("codes", this._panelCodes(this.openKey.split(":")[0])); this._renderPick()
      this._panelPart("foot", this._panelFoot(this.sub(p.id)))
    }
  }

  // Saves the editor's picks, one request at a time so quick clicks land in
  // order; the green toast shows once they're all in.
  _tpSave() {
    const p = this.tp
    const id = p.id
    const codes = [...p.codes].sort()
    p.saved = true
    p.pending = (p.pending || 0) + 1
    this._tpStatus("tp_saving")
    this._tpQueue = (this._tpQueue || Promise.resolve()).then(async () => {
      try {
        const data = await this._json("PATCH", this._subUrl(id), { codes })
        this._putSub(data.sub)
        if (this.draft && this.draft.id === id) this.draft.codes = new Set(this.sub(id).codes)
        this._refreshRows(id)
        p.pending -= 1
        // Once the last quick click is in: the green toast, and "Saved" in the editor.
        if (!p.pending) {
          const s = this.sub(id)
          this._toast(this.t("t_saved", { name: s.name, count: this.coverCount(s.codes) }))
          if (this.tp === p) this._tpStatus("tp_saved")
        }
        if (!this.tp) this.render()
      } catch (err) {
        this._toast(err.message, "error")
        if (this.tp === p) {
          p.pending = 0
          p.codes = new Set(this.sub(id).codes)
          this._renderTagPopParts()
          this._tpStatus("")
        }
      }
    })
  }

  // Picking a whole division takes the place of the single subdivisions in
  // it; with the whole division picked, a subdivision in it is left out
  // ("-09 91 00") or put back.
  _toggleCode(set, c) {
    const dv = c.slice(0, 2)
    if (!this.isDiv(c) && set.has(dv)) {
      const out = "-" + c
      if (set.has(out)) set.delete(out); else set.add(out)
      return
    }
    const mine = k => k !== c && (k.slice(0, 2) === c || (this.isOut(k) && k.slice(1, 3) === c))
    if (set.has(c)) {
      set.delete(c)
      if (this.isDiv(c)) [...set].forEach(k => { if (mine(k)) set.delete(k) })
      return
    }
    set.add(c)
    if (this.isDiv(c)) [...set].forEach(k => { if (mine(k)) set.delete(k) })
  }

  // A whole division first, then what it leaves out, then subdivisions, by division.
  _sortTags(list) {
    const key = c => (this.isOut(c) ? `${c.slice(1, 3)}~${c}` : c)
    return list.slice().sort((a, b) => (key(a) < key(b) ? -1 : key(a) > key(b) ? 1 : 0))
  }

  _renderPick() {
    const panel = this.listTarget.querySelector(".mlpanel")
    if (!panel || !this.pick) return
    const dv = panel.dataset.panel.split(":")[0]
    const q = this.pickQ.trim().toLowerCase()
    const list = this._pickList(dv, [...this.draft.codes], q)
    let head
    if (!q) head = dv === NONE || dv === ALL ? this.t("pick_head_all") : this.t("pick_head_div", { division: this.divLabel(dv) })
    else if (!list.length) head = this.t("pick_none", { q: this.pickQ.trim() })
    else if (list.length > PICK_LIMIT) head = this.t("pick_first", { limit: PICK_LIMIT, count: list.length })
    else head = this.t("pick_n", { count: list.length })
    panel.querySelector('[data-part="pickhead"]').textContent = head
    panel.querySelector('[data-part="pickchips"]').innerHTML = list.slice(0, PICK_LIMIT).map(c => this._pickChip(c, this.draft.codes, "draft-code")).join("")
  }

  _panelFoot(s) {
    const dirty = this._dirty()
    return `<button class="btn btn-secondary btn-sm dangerbtn" type="button" data-act="ask-remove" data-id="${s.id}">${this.te("remove_from_list")}</button><span class="grow"></span>${dirty ? `<span class="mlunsaved">${this.te("unsaved")}</span>` : ""}<button class="btn btn-secondary btn-sm" type="button" data-act="panel-cancel">${this.te("cancel")}</button><button class="btn btn-primary btn-sm" type="button" data-act="panel-save" ${dirty ? "" : "disabled"}>${this.te("save")}</button>`
  }

  _panelRatings(s) {
    const rt = this.rateOf(s)
    const mine = this.mine(s)
    const sum = rt.has ? this.t("rate_sum", { stars: rt.starsText, risk: rt.riskText, count: rt.n }) : this.t("no_ratings")
    const others = (s.rates || []).filter(r => !r.me && r.stars)
    let html = `<div class="mlph"><b>${this.te("ratings")}</b><span class="muted">${this._e(sum)}</span></div>
      <div class="fl"><span>${this.te("your_rating")}</span><span class="hint">${this.te("your_rating_hint")}</span>
        <div class="row8 mlwrap">${this._starsHtml(mine, "rate-star")}<div class="seg">${this._riskHtml(mine, "rate-risk")}</div></div>
        <input class="inp" type="text" value="${this._e(mine.note)}" data-rate-note="${s.id}" placeholder="${this.te("rate_note_ph")}" aria-label="${this.te("rate_note_aria")}"></div>`
    if (others.length) html += `<div class="fl"><span>${this.te("team_ratings")}</span>${this._othersHtml(others)}</div>`
    return html
  }

  _starsHtml(mine, act) {
    return `<div class="starpick" role="radiogroup" aria-label="${this.te("your_rating")}">${[1, 2, 3, 4, 5].map(n => `<button class="starbtn ${n <= (mine.stars || 0) ? "on" : ""}" type="button" role="radio" aria-checked="${n === mine.stars}" aria-label="${this.te("stars", { count: n })}" data-act="${act}" data-n="${n}">★</button>`).join("")}</div>`
  }

  _riskHtml(mine, act) {
    return ["low", "medium", "high"].map(r => `<button class="segbtn ${mine.risk === r ? "on" : ""}" type="button" data-act="${act}" data-v="${r}">${this.te(`risk_${r}`)}</button>`).join("")
  }

  _othersHtml(others) {
    return others.map(r => `<div class="mlother"><b>${this._e(r.who)}</b><span class="rstars">${this.stars(r.stars)}</span><span class="badge ${RISK_CLS[r.risk] || "b-gray"}">${this._e(this.riskLabel(r.risk))}</span><span class="grow">${r.note ? "“" + this._e(r.note) + "”" : ""}</span><span class="muted">${this._e(r.at ? this.t("rated_on", { date: r.at }) : "")}</span></div>`).join("")
  }

  _panelNotes(s) {
    const notes = s.notes || []
    let html = `<div class="mlph"><b>${this.te("notes")}</b><span class="muted">${notes.length ? this.te("n_notes", { count: notes.length }) : ""}</span></div>`
    if (!notes.length) html += `<span class="muted">${this.te("no_notes")}</span>`
    html += notes.map(n => `<div class="mlnote"><span class="muted">${this._e(n.by)} · ${this._e(n.at)}</span><span>${this._e(n.text)}</span></div>`).join("")
    html += `<div class="addform"><input class="inp" type="text" value="${this._e(this.noteDraft)}" data-note="${s.id}" placeholder="${this.te("note_ph")}" aria-label="${this.te("note_aria")}"><button class="btn btn-secondary btn-sm" type="button" data-act="note-add" data-id="${s.id}">${this.te("add_note")}</button></div>`
    return html
  }

  _panelFiles(s) {
    const files = (s.files || []).map(f => ({ ...f, up: false })).concat((this.uploads[s.id] || []).map(u => ({ ...u, up: true })))
    const done = (s.files || []).length
    let html = `<div class="mlph"><b>${this.te("files")}</b><span class="muted">${done ? this.te("n_files", { count: done }) : this.te("no_files")}</span></div>
      <label class="dz mldz" data-dz="${s.id}">${this.icons.upload}<span class="mldzt"><b>${this.te("drop")}</b><span>${this.te("drop_sub")}</span></span><span class="btn btn-secondary btn-sm">${this.te("add_files")}</span><input class="fhide" type="file" multiple data-files="${s.id}" aria-label="${this.te("add_files")}"></label>
      <div class="fchips">`
    html += files.map(f => {
      if (f.up) return `<span class="fchipn up" title="${this.te("uploading")}"><span class="spin2"></span><span class="trunc">${this._e(f.name)}</span><span class="muted">${this._e(f.size)}</span></span>`
      return `<span class="fchipn" title="${this.te("added_by", { who: f.by, date: f.at })}">${this.icons.doc}<a class="trunc" href="${this._e(f.url)}" target="_blank" rel="noopener" style="color: inherit; text-decoration: none;">${this._e(f.name)}</a><span class="muted">${this._e(f.size)}</span><button class="fx" type="button" data-act="file-remove" data-id="${s.id}" data-file="${f.id}" aria-label="${this.te("remove_file", { name: f.name })}">${this.icons.x}</button></span>`
    }).join("")
    return html + "</div>"
  }

  _panelPart(part, html) {
    const el = this.listTarget.querySelector(`.mlpanel [data-part="${part}"]`)
    if (el) el.innerHTML = html
  }

  _draftFrom(s) {
    return { id: s.id, name: s.name, contact: s.contact || "", email: s.email || "", phone: s.phone || "", area: s.area || "", ins: s.ins, insDate: s.insOn || "", codes: new Set(s.codes) }
  }

  _dirty() {
    const d = this.draft
    const s = d && this.sub(d.id)
    if (!s) return false
    const t = v => String(v == null ? "" : v).trim()
    return t(d.name) !== t(s.name) || t(d.contact) !== t(s.contact) || t(d.email) !== t(s.email) || t(d.phone) !== t(s.phone) ||
      t(d.area) !== t(s.area) || d.ins !== s.ins || [...d.codes].sort().join("|") !== s.codes.slice().sort().join("|") ||
      (this._datedIns(d.ins) && t(d.insDate) !== t(s.insOn))
  }

  _datedIns(ins) { return ins === "expiring" || ins === "expired" }

  // What the Insurance switch sends: its state, plus the date while it's Expiring / Expired.
  _insBody(ins, iso) {
    return this._datedIns(ins) ? { ins, insurance_expires_on: iso || "" } : { ins }
  }

  _warnUnsaved() {
    if (!this._dirty()) return
    const s = this.sub(this.draft.id)
    this._toast(this.t("t_unsaved", { name: s ? s.name : "" }))
  }

  openRow(dv, id) {
    const s = this.sub(id)
    if (!s) return
    this._warnUnsaved()
    this.openKey = `${dv}:${s.id}`
    this.draft = this._draftFrom(s)
    this.pick = false; this.pickQ = ""; this.noteDraft = ""
    this._renderList()
  }

  closeRow(quiet) {
    if (!quiet) this._warnUnsaved()
    this.openKey = null; this.draft = null; this.pick = false; this.pickQ = ""; this.noteDraft = ""
    this._renderList()
  }

  _deepOpen(id) {
    const s = this.sub(id)
    if (!s) return
    const first = s.codes.find(c => !this.isOut(c))
    const dv = this.view === "all" ? ALL : first ? first.slice(0, 2) : NONE
    this.closed.delete(dv)
    this.openRow(dv, s.id)
    const row = this.listTarget.querySelector(`.mlx[data-id="${s.id}"]`)
    if (row) row.scrollIntoView({ block: "center" })
  }

  async savePanel() {
    const d = this.draft
    if (!d) return
    const name = d.name.trim()
    const codes = [...d.codes].sort()
    if (!name) return this._toast(this.t("t_name_first"), "error")
    if (!codes.length) return this._toast(this.t("t_give_code"), "error")
    const dv = this.openKey.split(":")[0]
    try {
      const data = await this._json("PATCH", this._subUrl(d.id), { name, contact_name: d.contact, email: d.email, phone: d.phone, area: d.area, codes, ...this._insBody(d.ins, d.insDate) })
      this._putSub(data.sub)
      const s = this.sub(d.id)
      const stays = dv === ALL || codes.some(c => c.slice(0, 2) === dv)
      if (stays) {
        this.draft = this._draftFrom(s); this.pick = false; this.pickQ = ""
        this._toast(this.t("t_saved", { name: s.name, count: this.coverCount(s.codes) }))
      } else {
        this.openKey = null; this.draft = null; this.pick = false; this.pickQ = ""; this.noteDraft = ""
        this._toast(dv === NONE ? this.t("t_saved", { name: s.name, count: this.coverCount(s.codes) }) : this.t("t_moved", { name: s.name, division: this.divLabel(dv) }))
      }
      this.render()
    } catch (err) { this._toast(err.message, "error") }
  }

  // Your own stars / risk / note, saved as you click (shown right away).
  async rateSet(id, patch, rerenderModal) {
    const s = this.sub(id)
    if (!s) return
    const before = (s.rates || []).map(r => ({ ...r }))
    const mine = this.mine(s)
    const next = { ...mine, ...patch, me: true, who: mine.who || this.meName, at: mine.at || "" }
    const blank = !next.stars && !next.risk && !String(next.note || "").trim()
    s.rates = (s.rates || []).filter(r => !r.me).concat(blank ? [] : [next])
    this._afterRate(s, rerenderModal)
    try {
      const data = await this._json("PATCH", this._subUrl(id, "/rating"), { stars: next.stars || 0, risk: next.risk || "", note: next.note || "" })
      this._putSub(data.sub)
      this._afterRate(this.sub(id), rerenderModal)
    } catch (err) {
      s.rates = before
      this._afterRate(s, rerenderModal)
      this._toast(err.message, "error")
    }
  }

  _afterRate(s, rerenderModal) {
    this._refreshRows(s.id)
    if (this.draft && this.draft.id === s.id) this._panelPart("ratings", this._panelRatings(s))
    if (rerenderModal && this.add && this.add.editId === s.id) this._renderAddRate()
  }

  async addNote(id, text, fromModal) {
    const body = String(text || "").trim()
    if (!body) return this._toast(this.t("t_note_first"), "error")
    try {
      const data = await this._json("POST", this._subUrl(id, "/notes"), { body })
      this._putSub(data.sub)
      const s = this.sub(id)
      this._refreshRows(id)
      if (fromModal) { this.add.note = ""; this._renderAddRate() } else {
        this.noteDraft = ""
        if (this.draft && this.draft.id === s.id) {
          this._panelPart("notes", this._panelNotes(s))
          const input = this.listTarget.querySelector(".mlpanel [data-note]")
          if (input) input.focus()
        }
      }
    } catch (err) { this._toast(err.message, "error") }
  }

  async uploadFiles(id, list) {
    const files = Array.from(list || [])
    const s = this.sub(id)
    if (!files.length || !s) return
    const tmp = files.map(f => ({ tmp: `u${++this.seq}`, name: f.name, size: this._fsize(f.size) }))
    this.uploads[s.id] = (this.uploads[s.id] || []).concat(tmp)
    this._refreshFiles(s.id)
    try {
      const presign = await this._json("POST", this.presignUrlValue, { files: files.map(f => ({ filename: f.name, content_type: f.type || "application/octet-stream", byte_size: f.size })) })
      await Promise.all(presign.uploads.map((u, i) => fetch(u.upload_url, { method: "PUT", body: files[i], headers: { "Content-Type": u.content_type || files[i].type || "application/octet-stream" } })
        .then(r => { if (!r.ok) throw new Error(this.t("t_error")) })))
      const data = await this._json("POST", this._subUrl(s.id, "/files"), { files: presign.uploads.map((u, i) => ({ cache_name: u.cache_name, filename: files[i].name, content_type: files[i].type, byte_size: files[i].size })) })
      this._putSub(data.sub)
      this._toast(files.length === 1 ? this.t("t_file_saved", { name: files[0].name, sub: s.name }) : this.t("t_files_saved", { count: files.length, sub: s.name }))
    } catch (err) {
      this._toast(err.message, "error")
    } finally {
      const ids = tmp.map(t => t.tmp)
      this.uploads[s.id] = (this.uploads[s.id] || []).filter(u => !ids.includes(u.tmp))
      this._refreshFiles(s.id)
    }
  }

  async removeFile(id, fileId) {
    const s = this.sub(id)
    const file = s && (s.files || []).find(f => f.id === Number(fileId))
    if (!file) return
    try {
      const data = await this._json("DELETE", this._subUrl(id, `/files/${fileId}`))
      this._putSub(data.sub)
      this._refreshFiles(id)
      this._toast(this.t("t_file_removed", { name: file.name }))
    } catch (err) { this._toast(err.message, "error") }
  }

  _refreshFiles(id) {
    this._refreshRows(id)
    if (this.draft && this.draft.id === Number(id)) this._panelPart("files", this._panelFiles(this.sub(id)))
  }

  // ── Delegated events (list, filters, the Add pop-up) ──────────────────
  _onClick(e) {
    const el = e.target.closest("[data-act]")
    if (!el || !e.currentTarget.contains(el)) return
    const act = el.dataset.act
    const id = el.dataset.id ? Number(el.dataset.id) : null
    switch (act) {
      case "area": this.area = el.dataset.v; this._renderAreas(); this._renderList(); break
      case "div-toggle": {
        const dv = el.dataset.dv
        if (this.closed.has(dv)) this.closed.delete(dv); else this.closed.add(dv)
        this._renderList(); break
      }
      case "add-in": e.stopPropagation(); this.openAdd(null, null, el.dataset.dv); break
      case "tag-edit": e.stopPropagation(); this.openTagPop(el, id, el.dataset.dv); break
      case "tp-draft": e.stopPropagation(); if (this.draft) this.openTagPop(el, this.draft.id, el.dataset.dv, true); break
      case "tp-close": this.closeTagPop(); break
      case "tp-code": this._tpToggle(el.dataset.code); break
      case "row-toggle":
        e.stopPropagation()
        if (this.openKey === `${el.dataset.dv}:${id}`) this.closeRow(); else this.openRow(el.dataset.dv, id)
        break
      case "row-open":
        e.stopPropagation()
        if (this.openKey !== `${el.dataset.dv}:${id}`) this.openRow(el.dataset.dv, id)
        break
      case "draft-ins":
        this.draft.ins = el.dataset.v
        this._panelPart("ins", this._segHtml(this.draft.ins, "draft-ins"))
        this._panelPart("insdate", this._insDateHtml(this.draft.ins, this.draft.insDate, 'data-draft="insDate"'))
        this._panelPart("foot", this._panelFoot(this.sub(this.draft.id)))
        break
      case "draft-code": {
        this._toggleCode(this.draft.codes, el.dataset.code)
        this._panelPart("codes", this._panelCodes(this.openKey.split(":")[0])); this._renderPick()
        this._panelPart("foot", this._panelFoot(this.sub(this.draft.id)))
        break
      }
      case "pick-toggle":
        this.pick = !this.pick; this.pickQ = ""
        this._panelPart("codes", this._panelCodes(this.openKey.split(":")[0])); this._renderPick()
        if (this.pick) { const i = this.listTarget.querySelector('.mlpanel [data-input="pickq"]'); if (i) i.focus() }
        break
      case "panel-save": this.savePanel(); break
      case "panel-cancel": this.closeRow(true); break
      case "ask-remove": this.openRemove(id); break
      case "rate-star": { const s = this.sub(this.draft.id); const n = Number(el.dataset.n); this.rateSet(s.id, { stars: n === this.mine(s).stars ? 0 : n }); break }
      case "rate-risk": { const s = this.sub(this.draft.id); const v = el.dataset.v; this.rateSet(s.id, { risk: this.mine(s).risk === v ? "" : v }); break }
      case "note-add": this.addNote(id, this.noteDraft); break
      case "file-remove": e.preventDefault(); this.removeFile(id, el.dataset.file); break
      case "cf-toggle": this.cfOpen = !this.cfOpen; this.cfQ = ""; this._renderCf(); if (this.cfOpen) { const i = this.cfTarget.querySelector('[data-input="cfq"]'); if (i) i.focus() } break
      case "cf-close": this.cfOpen = false; this.cfQ = ""; this._renderCf(); break
      case "cf-clear": this.cf.clear(); this._renderCf(); this._renderCfOn(); this._renderList(); break
      case "cf-remove": this.cf.delete(el.dataset.code); this._renderCf(); this._renderCfOn(); this._renderList(); break
      // The Add / Edit pop-up
      case "add-ins":
        this.add.ins = el.dataset.v
        this.addInsTarget.innerHTML = this._segHtml(this.add.ins, "add-ins")
        this.addInsDateTarget.innerHTML = this._insDateHtml(this.add.ins, this.add.insDate, 'data-add="ins_date"')
        break
      case "add-code": {
        this._toggleCode(this.add.codes, el.dataset.code)
        this._renderAddCodes(); break
      }
      case "add-star": { const n = Number(el.dataset.n); this.add.rate.stars = n === this.add.rate.stars ? 0 : n; this._renderAddRate(); break }
      case "add-risk": { const v = el.dataset.v; this.add.rate.risk = this.add.rate.risk === v ? "" : v; this._renderAddRate(); break }
      case "add-note-now": this.addNote(this.add.editId, this.add.note, true); break
    }
  }

  _onInput(e) {
    const el = e.target
    if (el.dataset.draft && this.draft) {
      this.draft[el.dataset.draft] = el.value
      this._panelPart("foot", this._panelFoot(this.sub(this.draft.id)))
    } else if (el.dataset.input === "pickq") {
      this.pickQ = el.value; this._renderPick()
    } else if (el.dataset.input === "tpq" && this.tp) {
      this.tp.q = el.value; this._renderTagPopParts()
    } else if (el.dataset.input === "cfq") {
      this.cfQ = el.value
      const rows = this.cfTarget.querySelector('[data-part="cfrows"]')
      if (rows) rows.innerHTML = this._cfRows()
    } else if (el.dataset.note) {
      this.noteDraft = el.value
    } else if (el.dataset.add && this.add) {
      if (el.dataset.add === "rate_note") this.add.rate.note = el.value
      else if (el.dataset.add === "note") this.add.note = el.value
    }
  }

  _onChange(e) {
    const el = e.target
    if (el.dataset.draft && this.draft) {
      this.draft[el.dataset.draft] = el.value
      this._panelPart("foot", this._panelFoot(this.sub(this.draft.id)))
      return
    }
    if (el.dataset.add === "ins_date" && this.add) { this.add.insDate = el.value; return }
    if (el.dataset.cf) {
      if (el.checked) this.cf.add(el.dataset.cf); else this.cf.delete(el.dataset.cf)
      const btn = this.cfTarget.querySelector(".mlfbtn")
      if (btn) { btn.classList.toggle("on", this.cf.size > 0); btn.querySelector('[data-part="cflabel"]').textContent = this._cfLabel() }
      const foot = this.cfTarget.querySelector('[data-part="cffoot"]')
      if (foot) foot.textContent = this.cf.size ? this.t("cf_foot_n", { count: this.cf.size }) : this.t("cf_foot_none")
      const clear = this.cfTarget.querySelector('.mlfmh [data-act="cf-clear"]')
      if (clear) clear.disabled = this.cf.size === 0
      this._renderCfOn(); this._renderList()
    } else if (el.dataset.rateNote) {
      const s = this.sub(el.dataset.rateNote)
      if (s && el.value.trim() !== String(this.mine(s).note || "").trim()) this.rateSet(s.id, { note: el.value.trim() })
    } else if (el.dataset.files) {
      this.uploadFiles(Number(el.dataset.files), el.files)
      el.value = ""
    }
  }

  _onKeydown(e) {
    const el = e.target
    if (e.key !== "Enter") return
    if (el.dataset.note) { e.preventDefault(); this.addNote(Number(el.dataset.note), el.value) }
    else if (el.dataset.rateNote) { e.preventDefault(); el.blur() }
    else if (el.dataset.add === "note") { e.preventDefault(); this.addNote(this.add.editId, el.value, true) }
    else if (el.dataset.input) e.preventDefault()
  }

  _onDrag(e) {
    const dz = e.target.closest && e.target.closest(".mldz")
    if (!dz) return
    e.preventDefault()
    if (e.type === "dragenter" || e.type === "dragover") dz.classList.add("over")
    else if (e.type === "dragleave") { if (!dz.contains(e.relatedTarget)) dz.classList.remove("over") }
    else if (e.type === "drop") { dz.classList.remove("over"); this.uploadFiles(Number(dz.dataset.dz), e.dataTransfer && e.dataTransfer.files) }
  }

  // ── Toolbar ───────────────────────────────────────────────────────────
  search(e) {
    this.q = e.target.value
    this._renderList()
  }

  expandAll() {
    this.closed.clear()
    this._renderList()
  }

  collapseAll() {
    const ix = this._index()
    this.closed = new Set([...ix.divs, NONE])
    this.q = ""; this.area = "all"; this.cf.clear()
    this.openKey = null; this.draft = null; this.pick = false; this.pickQ = ""; this.noteDraft = ""
    this.searchTarget.value = ""
    this.render()
  }

  // ── Add a sub / Edit sub ──────────────────────────────────────────────
  openAdd(e, code, dv) {
    const codes = new Set()
    if (code) codes.add(code)
    if (dv) {
      const have = this._index().allCodes.filter(c => c.slice(0, 2) === dv)
      if (have.length === 1) codes.add(have[0])
    }
    this.add = { editId: null, div: dv || null, f: { name: "", contact_name: "", email: "", phone: "", area: "" }, ins: "ok", insDate: "", codes, q: "" }
    this._showAdd()
  }

  openEdit(id) {
    const s = this.sub(id)
    if (!s) return
    const mine = this.mine(s)
    this.add = { editId: s.id, div: null, f: { name: s.name, contact_name: s.contact, email: s.email, phone: s.phone, area: s.area }, ins: s.ins, insDate: s.insOn || "",
      codes: new Set(s.codes), q: "", rate: { stars: mine.stars || 0, risk: mine.risk || "", note: mine.note || "" }, rate0: { stars: mine.stars || 0, risk: mine.risk || "", note: mine.note || "" }, note: "" }
    this._showAdd()
  }

  _showAdd() {
    const a = this.add
    const title = a.editId ? this.t("edit_title") : (a.div ? this.t("add_in", { division: this.divLabel(a.div) }) : this.t("add_title"))
    this.addTitleTarget.textContent = title
    this.addDialogTarget.setAttribute("aria-label", title)
    this.addFormTarget.querySelectorAll("input[name]").forEach(i => { i.value = a.f[i.name] || "" })
    this.addCodeSearchTarget.value = ""
    this.addInsTarget.innerHTML = this._segHtml(a.ins, "add-ins")
    this.addInsDateTarget.innerHTML = this._insDateHtml(a.ins, a.insDate, 'data-add="ins_date"')
    this._renderAddCodes()
    this.addRateTarget.style.display = a.editId ? "" : "none"
    this.addRemoveTarget.style.display = a.editId ? "" : "none"
    if (a.editId) this._renderAddRate()
    this.addModalTarget.style.display = ""
    const first = this.addFormTarget.querySelector('input[name="name"]')
    if (first && !a.editId) first.focus()
  }

  addCodeSearch(e) {
    this.add.q = e.target.value
    this._renderAddCodes()
  }

  _renderAddCodes() {
    const a = this.add
    const q = a.q.trim().toLowerCase()
    let list
    if (q) {
      list = this._pickList(null, [...a.codes], q).slice(0, 40)
    } else {
      const pool = new Set(this._index().allCodes)
      a.codes.forEach(c => pool.add(c.replace(/^-/, "")))
      if (a.div) this.csi.forEach(([c, , common]) => { if (common && c.slice(0, 2) === a.div) pool.add(c) })
      if (!this._index().allCodes.length) this.csi.forEach(([c, , common]) => { if (common) pool.add(c) })
      // Each division's "All of" chip, just ahead of its subdivisions.
      ;[...pool].forEach(c => pool.add(c.slice(0, 2)))
      list = [...pool].sort()
      if (a.div) list = list.filter(c => c.slice(0, 2) === a.div).concat(list.filter(c => c.slice(0, 2) !== a.div))
    }
    this.addCodesLabelTarget.textContent = this.t("codes_n", { count: [...a.codes].filter(c => !this.isOut(c)).length })
    this.addCodesTarget.innerHTML = list.map(c => this._pickChip(c, a.codes, "add-code")).join("") ||
      `<span class="muted">${this.te("pick_none", { q: a.q.trim() })}</span>`
  }

  _renderAddRate() {
    const a = this.add
    const s = this.sub(a.editId)
    if (!s) return
    const others = (s.rates || []).filter(r => !r.me && r.stars)
    let html = `<div class="fl"><span>${this.te("your_rating")}</span><span class="hint">${this.te("your_rating_hint_modal")}</span>
      <div class="row8 mlwrap">${this._starsHtml(a.rate, "add-star")}<div class="seg">${this._riskHtml(a.rate, "add-risk")}</div></div>
      <input class="inp" type="text" value="${this._e(a.rate.note)}" data-add="rate_note" placeholder="${this.te("rate_note_ph")}" aria-label="${this.te("rate_note_aria")}"></div>`
    if (others.length) html += `<div class="fl"><span>${this.te("team_ratings")}</span>${this._othersHtml(others)}</div>`
    html += `<div class="fl"><span>${this.te("notes_about")}</span>${(s.notes || []).map(n => `<div class="mlnote"><span class="muted">${this._e(n.by)} · ${this._e(n.at)}</span><span>${this._e(n.text)}</span></div>`).join("")}
      <div class="addform"><input class="inp" type="text" value="${this._e(a.note)}" data-add="note" placeholder="${this.te("note_ph")}" aria-label="${this.te("note_aria")}"><button class="btn btn-secondary btn-sm" type="button" data-act="add-note-now">${this.te("add_note")}</button></div></div>`
    this.addRateTarget.innerHTML = html
  }

  closeAdd(e) {
    if (e && e.type === "click" && e.currentTarget === this.addModalTarget && e.target !== this.addModalTarget) return
    this.addModalTarget.style.display = "none"
    this.add = null
  }

  async saveAdd() {
    const a = this.add
    if (!a) return
    const f = {}
    this.addFormTarget.querySelectorAll("input[name]").forEach(i => { f[i.name] = i.value.trim() })
    const codes = [...a.codes].sort()
    if (!f.name) return this._toast(this.t("t_name_first"), "error")
    if (!codes.length) return this._toast(this.t("t_pick_code"), "error")
    this.addSaveTarget.disabled = true
    try {
      const body = { ...f, codes, ...this._insBody(a.ins, a.insDate) }
      const data = a.editId ? await this._json("PATCH", this._subUrl(a.editId), body) : await this._json("POST", this.createUrlValue, body)
      this._putSub(data.sub)
      const id = data.sub.id
      if (a.editId) {
        const r = a.rate, r0 = a.rate0
        if (r.stars !== r0.stars || r.risk !== r0.risk || r.note.trim() !== r0.note.trim()) {
          const rd = await this._json("PATCH", this._subUrl(id, "/rating"), { stars: r.stars || 0, risk: r.risk || "", note: r.note.trim() })
          this._putSub(rd.sub)
        }
        if (a.note.trim()) {
          const nd = await this._json("POST", this._subUrl(id, "/notes"), { body: a.note.trim() })
          this._putSub(nd.sub)
        }
      }
      if (this.draft && this.draft.id === id) this.draft = this._draftFrom(this.sub(id))
      this.closeAdd()
      this.render()
      const s = this.sub(id)
      this._toast(a.editId ? this.t("t_saved", { name: s.name, count: this.coverCount(s.codes) }) : this.t("t_added", { name: s.name, count: this.coverCount(s.codes) }))
    } catch (err) {
      this._toast(err.message, "error")
    } finally {
      this.addSaveTarget.disabled = false
    }
  }

  askRemoveFromModal() {
    if (this.add && this.add.editId) this.openRemove(this.add.editId)
  }

  // ── Remove ────────────────────────────────────────────────────────────
  openRemove(id) {
    const s = this.sub(id)
    if (!s) return
    this.rmId = s.id
    this.rmTitleTarget.textContent = this.t("rm_title", { name: s.name })
    this.rmDialogTarget.setAttribute("aria-label", this.t("rm_title", { name: s.name }))
    this.rmTextTarget.textContent = this.t("rm_text", { name: s.name })
    const lines = []
    const group = (list, key) => {
      const byJob = {}
      list.forEach(j => { (byJob[j.job] = byJob[j.job] || []).push(j.what) })
      Object.keys(byJob).forEach(job => lines.push(this.t(key, { what: [...new Set(byJob[job])].join(", "), job })))
    }
    group((s.jobs || []).filter(j => !j.invited), "rm_picked")
    group((s.jobs || []).filter(j => j.invited), "rm_invited")
    this.rmJobsTarget.innerHTML = lines.length ? `<span>${lines.map(l => this._e(l)).join(" ")}</span>` : ""
    this.rmJobsTarget.style.display = lines.length ? "" : "none"
    this.rmModalTarget.style.display = ""
  }

  closeRemove(e) {
    if (e && e.type === "click" && e.currentTarget === this.rmModalTarget && e.target !== this.rmModalTarget) return
    this.rmModalTarget.style.display = "none"
    this.rmId = null
  }

  async confirmRemove() {
    const s = this.sub(this.rmId)
    if (!s) return this.closeRemove()
    this.rmGoTarget.disabled = true
    try {
      await this._json("DELETE", this._subUrl(s.id))
      this.subs = this.subs.filter(x => x.id !== s.id)
      if (this.draft && this.draft.id === s.id) { this.openKey = null; this.draft = null; this.pick = false; this.pickQ = ""; this.noteDraft = "" }
      if (this.add && this.add.editId === s.id) this.closeAdd()
      this.closeRemove()
      this.render()
      this._toast(this.t("t_removed", { name: s.name }))
    } catch (err) {
      this._toast(err.message, "error")
    } finally {
      this.rmGoTarget.disabled = false
    }
  }

  // ── Import your sub list ──────────────────────────────────────────────
  openImport() {
    this.imp = { state: "drop" }
    this._renderImp()
    this.impModalTarget.style.display = ""
  }

  closeImport(e) {
    if (e && e.type === "click" && e.currentTarget === this.impModalTarget && e.target !== this.impModalTarget) return
    this.impModalTarget.style.display = "none"
    this.imp = null
  }

  impDragOver(e) { e.preventDefault(); this.impDropTarget.style.borderColor = "#4990e2" }
  impDragLeave(e) { if (!this.impDropTarget.contains(e.relatedTarget)) this.impDropTarget.style.borderColor = "" }
  impDrop(e) {
    e.preventDefault()
    this.impDropTarget.style.borderColor = ""
    const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]
    if (file) this._impFile(file)
  }
  impPicked(e) {
    const file = e.target.files && e.target.files[0]
    e.target.value = ""
    if (file) this._impFile(file)
  }

  async _impFile(file) {
    if (!/\.(xlsx|xlsm|csv)$/i.test(file.name)) return this._toast(this.t("imp_bad"), "error")
    this.imp = { state: "reading", name: file.name }
    this._renderImp()
    try {
      const presign = await this._json("POST", this.presignUrlValue, { files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
      const u = presign.uploads[0]
      const put = await fetch(u.upload_url, { method: "PUT", body: file, headers: { "Content-Type": u.content_type || file.type || "application/octet-stream" } })
      if (!put.ok) throw new Error(this.t("t_error"))
      const data = await this._json("POST", this.importPreviewUrlValue, { cache_name: u.cache_name, filename: file.name })
      if (!this.imp) return
      this.imp = { state: "ready", name: file.name, cacheName: u.cache_name, preview: data.preview }
    } catch (err) {
      if (!this.imp) return
      this.imp = { state: "drop" }
      this._toast(err.message, "error")
    }
    this._renderImp()
  }

  // A workbook's tabs: the import picks the one that reads most like a sub
  // list; the viewer can read another one instead.
  async impSheet(e) {
    const im = this.imp
    if (!im || im.state !== "ready") return
    const was = im.preview
    this.imp = { ...im, state: "reading" }
    this._renderImp()
    try {
      const data = await this._json("POST", this.importPreviewUrlValue, { cache_name: im.cacheName, filename: im.name, sheet: e.target.value })
      if (!this.imp) return
      this.imp = { ...im, state: "ready", preview: data.preview }
    } catch (err) {
      if (!this.imp) return
      this.imp = { ...im, state: "ready", preview: was }
      this._toast(err.message, "error")
    }
    this._renderImp()
  }

  _renderImp() {
    const im = this.imp
    let html = ""
    if (im.state === "reading") {
      html = `<div class="filechip"><span class="spin2"></span><span>${this._e(im.name)}</span><span class="muted">${this.te("imp_reading")}</span></div>`
    } else if (im.state === "ready") {
      const p = im.preview
      const note = [this.t("imp_found", { count: p.found })]
      if (p.dupes) note.push(this.t("imp_dupes", { count: p.dupes }))
      if (p.already) note.push(this.t("imp_already", { count: p.already }))
      if (p.no_email) note.push(this.t("imp_no_email", { count: p.no_email }))
      if (p.no_trade) note.push(this.t("imp_no_trade", { count: p.no_trade }))
      const tabs = (p.sheets || []).length > 1
        ? `<label class="row8" style="align-items: center; font-size: 13px;"><span class="muted">${this.te("imp_tab")}</span>
            <select class="inp" style="width: auto; height: 32px;" data-action="change->precon-subs#impSheet">${p.sheets.map(s => `<option value="${this._e(s)}"${s === p.sheet ? " selected" : ""}>${this._e(s)}</option>`).join("")}</select></label>`
        : ""
      html = `<div class="filechip">${this.icons.doc}<span>${this._e(im.name)}</span><span class="muted">${this.te("imp_rows", { count: p.rows })}</span></div>
        ${tabs}
        <div class="mapttl">${this.te("imp_matched")}</div>
        <div class="map">${p.mapping.map(m => `<div class="maprow"><span>${this._e(m.from || this.t("imp_col", { col: m.col }))}</span>${this.icons.arrow}<span class="strong">${this._e(this.t(`imp_to.${m.to}`))}</span></div>`).join("")}</div>
        <div class="ai">${this.icons.sparkles}<div>${this._e(note.join(" "))}</div></div>`
    }
    this.impResultTarget.innerHTML = html
    this.impResultTarget.style.display = html ? "" : "none"
    const ready = im.state === "ready" && im.preview.found > 0
    this.impGoTarget.disabled = !ready
    this.impGoTarget.textContent = ready ? this.t("imp_btn", { count: im.preview.found }) : this.t("imp_btn_empty")
  }

  async runImport() {
    const im = this.imp
    if (!im || im.state !== "ready") return
    this.impGoTarget.disabled = true
    try {
      const data = await this._json("POST", this.importUrlValue, { cache_name: im.cacheName, filename: im.name, sheet: im.preview.sheet || "" })
      this.subs = data.subs || this.subs
      this.closeImport()
      this.render()
      this._toast(this.t("imp_done", { count: data.result.added + data.result.updated }))
    } catch (err) {
      this.impGoTarget.disabled = false
      this._toast(err.message, "error")
    }
  }

  async importLists() {
    this.impListsBtnTarget.disabled = true
    try {
      const data = await this._json("POST", this.importListsUrlValue)
      this.subs = data.subs || this.subs
      this.closeImport()
      this.render()
      const [a, b] = data.imported || [0, 0]
      this._toast(a + b > 0 ? this.t("imp_lists_done", { directory: a, sub_bids: b }) : this.t("imp_lists_none"))
    } catch (err) {
      this._toast(err.message, "error")
    } finally {
      this.impListsBtnTarget.disabled = false
    }
  }

  // ── ⋮ menu: Delete entire list ───────────────────────────────────────
  toggleMore() {
    if (this.moreMenuTarget.style.display !== "none") return this._closeMore()
    this.moreMenuTarget.style.display = ""
    this._outside = (e) => { if (!this.moreWrapTarget.contains(e.target)) this._closeMore() }
    document.addEventListener("click", this._outside)
  }

  _closeMore() {
    if (this.hasMoreMenuTarget) this.moreMenuTarget.style.display = "none"
    if (this._outside) document.removeEventListener("click", this._outside)
    this._outside = null
  }

  askRemoveAll() {
    this._closeMore()
    const n = this.subs.length
    if (!n) return this._toast(this.t("ra_empty"))
    this.raTextTarget.textContent = this.t("ra_text", { count: n })
    this.raGoTarget.textContent = this.t("ra_btn", { count: n })
    this.raModalTarget.style.display = ""
  }

  closeRemoveAll(e) {
    if (e && e.type === "click" && e.currentTarget === this.raModalTarget && e.target !== this.raModalTarget) return
    this.raModalTarget.style.display = "none"
  }

  async confirmRemoveAll() {
    this.raGoTarget.disabled = true
    try {
      const data = await this._json("POST", this.removeAllUrlValue, {})
      this.subs = []
      this.openKey = null; this.draft = null; this.pick = false; this.pickQ = ""; this.noteDraft = ""
      this.closeRemoveAll()
      this.render()
      this._toast(this.t("t_removed_all", { count: data.count }))
    } catch (err) {
      this._toast(err.message, "error")
    } finally {
      this.raGoTarget.disabled = false
    }
  }

  // ── History ───────────────────────────────────────────────────────────
  openHistory() {
    this.hist = { tab: "all", loading: true, busy: false, entries: [], deleted: [], more: false }
    this.histModalTarget.style.display = ""
    this._renderHist()
    this._loadHist()
  }

  closeHistory(e) {
    if (e && e.type === "click" && e.currentTarget === this.histModalTarget && e.target !== this.histModalTarget) return
    this.histModalTarget.style.display = "none"
    this.hist = null
  }

  // The first page (and the deleted subs), or the next older page.
  async _loadHist(older = false) {
    const h = this.hist
    if (!h) return
    try {
      const last = h.entries[h.entries.length - 1]
      const data = await this._json("GET", this.historyUrlValue + (older && last ? `?before=${last.id}` : ""))
      if (this.hist !== h) return
      h.entries = older ? h.entries.concat(data.entries) : data.entries
      if (!older) h.deleted = data.deleted || []
      h.more = !!data.more
    } catch (err) {
      this._toast(err.message, "error")
    }
    h.loading = false
    if (this.hist === h) this._renderHist()
  }

  histTab(e) {
    if (!this.hist) return
    this.hist.tab = e.currentTarget.dataset.tab
    this._renderHist()
  }

  histMore() {
    if (!this.hist || this.hist.loading) return
    this.hist.loading = true
    this._renderHist()
    this._loadHist(true)
  }

  async histRestore(e) {
    const h = this.hist
    if (!h || h.busy) return
    h.busy = true
    this._renderHist()
    try {
      const data = await this._json("POST", this.restoreUrlValue.replace("__ID__", e.currentTarget.dataset.id), {})
      this._putSub(data.sub)
      this.render()
      this._toast(this.t("t_restored", { name: data.sub.name }))
      await this._loadHist()
    } catch (err) {
      this._toast(err.message, "error")
    }
    h.busy = false
    if (this.hist === h) this._renderHist()
  }

  // Every deleted sub, or (data-change) the ones one whole-list delete took off.
  async histRestoreAll(e) {
    const h = this.hist
    if (!h || h.busy) return
    const change = e.currentTarget.dataset.change
    h.busy = true
    this._renderHist()
    try {
      const data = await this._json("POST", this.restoreAllUrlValue, change ? { change_id: Number(change) } : {})
      this.subs = data.subs || this.subs
      this.render()
      const msg = [this.t("t_restored_all", { count: data.restored })]
      if (data.skipped) msg.push(this.t("t_restored_skipped", { count: data.skipped }))
      this._toast(msg.join(" "), data.restored ? "notice" : "error")
      await this._loadHist()
    } catch (err) {
      this._toast(err.message, "error")
    }
    h.busy = false
    if (this.hist === h) this._renderHist()
  }

  _renderHist() {
    const h = this.hist
    if (!h) return
    const tab = (key, label) => `<button class="segbtn${h.tab === key ? " on" : ""}" type="button" data-tab="${key}" data-action="precon-subs#histTab">${this._e(label)}</button>`
    this.histTabsTarget.innerHTML = `<div class="seg">${tab("all", this.t("hist_all"))}${tab("deleted", this.t("hist_deleted", { count: h.deleted.length }))}</div>`
    const off = h.busy ? " disabled" : ""
    let body = ""
    let foot = ""
    if (h.loading && !h.entries.length) {
      body = `<div class="hempty"><span class="spin2"></span>${this.te("hist_loading")}</div>`
    } else if (h.tab === "deleted") {
      body = h.deleted.length ? h.deleted.map(d => this._histDeletedRow(d, off)).join("") : `<div class="hempty">${this.te("hist_none_deleted")}</div>`
      const back = h.deleted.filter(d => !d.taken).length
      if (back) foot = `<button class="btn btn-primary" type="button" data-action="precon-subs#histRestoreAll"${off}>${this.icons.restore || ""}${this.te("hist_restore_all", { count: back })}</button>`
    } else {
      body = h.entries.length ? h.entries.map(x => this._histRow(x, off)).join("") : `<div class="hempty">${this.te("hist_none")}</div>`
      if (h.more) body += `<div class="hempty"><button class="linkbtn" type="button" data-action="precon-subs#histMore"${h.loading ? " disabled" : ""}>${this.te(h.loading ? "hist_loading" : "hist_more")}</button></div>`
    }
    this.histBodyTarget.innerHTML = body
    this.histFootTarget.innerHTML = foot
  }

  _histRow(x, off) {
    const r = x.restore
    let btn = ""
    if (r && r.change_id) btn = `<button class="btn btn-secondary btn-sm" type="button" data-change="${r.change_id}" data-action="precon-subs#histRestoreAll"${off}>${this.icons.restore || ""}${this.te("hist_restore_n", { count: r.count })}</button>`
    else if (r) btn = `<button class="btn btn-secondary btn-sm" type="button" data-id="${r.id}" data-action="precon-subs#histRestore"${off}>${this.icons.restore || ""}${this.te("hist_restore")}</button>`
    const lines = (x.lines || []).length ? `<ul class="hlines">${x.lines.map(l => `<li>${this._e(l)}</li>`).join("")}</ul>` : ""
    return `<div class="hrow"><div class="grow"><div class="htitle">${this._e(x.title)}</div><div class="hmeta">${this._e(x.who)} · ${this._e(this._when(x.at))}</div>${lines}</div>${btn}</div>`
  }

  _histDeletedRow(d, off) {
    const codes = d.codes || []
    const shown = codes.slice(0, 4).map(c => this.codeLabel(c)).join(", ") + (codes.length > 4 ? ` +${codes.length - 4}` : "")
    const meta = d.who ? this.t("hist_deleted_by", { when: this._when(d.at), who: d.who }) : this.t("hist_deleted_on", { when: this._when(d.at) })
    const btn = d.taken
      ? `<span class="hmeta">${this.te("hist_taken")}</span>`
      : `<button class="btn btn-secondary btn-sm" type="button" data-id="${d.id}" data-action="precon-subs#histRestore"${off}>${this.icons.restore || ""}${this.te("hist_restore")}</button>`
    return `<div class="hrow"><div class="grow"><div class="htitle">${this._e(d.name)}</div><div class="hmeta">${this._e(meta)}</div>${codes.length ? `<ul class="hlines"><li>${this._e(shown)}</li></ul>` : ""}</div>${btn}</div>`
  }

  _when(iso) {
    if (!iso) return ""
    const opts = { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }
    try { return new Date(iso).toLocaleString(document.documentElement.lang || undefined, opts) } catch (_) { return new Date(iso).toLocaleString(undefined, opts) }
  }

  // ── Plumbing ──────────────────────────────────────────────────────────
  stop(e) { e.stopPropagation() }

  _closeTop() {
    if (this.tp) return this.closeTagPop()
    if (this._outside) return this._closeMore()
    if (this.raModalTarget.style.display !== "none") return this.closeRemoveAll()
    if (this.rmModalTarget.style.display !== "none") return this.closeRemove()
    if (this.addModalTarget.style.display !== "none") return this.closeAdd()
    if (this.impModalTarget.style.display !== "none") return this.closeImport()
    if (this.histModalTarget.style.display !== "none") return this.closeHistory()
    if (this.cfOpen) { this.cfOpen = false; this.cfQ = ""; this._renderCf() }
  }

  _subUrl(id, suffix = "") { return this.subUrlValue.replace("__ID__", id) + suffix }

  // Put the server's copy of a sub in the list, kept in name order.
  _putSub(sub) {
    if (!sub) return
    const i = this.subs.findIndex(s => s.id === sub.id)
    if (i >= 0) this.subs[i] = sub; else this.subs.push(sub)
    this.subs.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
  }

  _fsize(n) {
    if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`
    return `${Math.max(1, Math.round(n / 1024))} KB`
  }

  _toast(message, type = "notice") {
    const toast = document.createElement("div")
    toast.className = type === "error" ? "flash-toast-alert" : "flash-toast-notice"
    toast.setAttribute("data-controller", "flash")
    toast.setAttribute("data-flash-dismiss-after-value", "5000")
    toast.textContent = message
    document.body.prepend(toast)
  }

  async _json(method, url, body) {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token }, body: body === undefined ? undefined : JSON.stringify(body) })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || data.ok === false) throw new Error(data.error || this.t("t_error"))
    return data
  }
}
